import { useEffect, useRef, useState } from 'react'
import { EVENT_VOICE_ANIMATION_FOR_INTERVIEW_PREP } from 'builder/views/Interview/constants'
import { VoiceAnimationEnum, VoiceAnimationEvent } from 'builder/modules/interview/types'
import { Animation, Children, Wrapper } from './styles'

interface Props {
  children: React.ReactChild
}

const ANIMATION_SIZE = 124

export const VoiceAnimation = ({ children }: Props) => {
  const wrapperRef = useRef<HTMLDivElement | null>(null)
  const animationDivRef = useRef<HTMLDivElement | null>(null)
  const [maxScale, setMaxScale] = useState<number>(5)

  useEffect(() => {
    const calculateMaxScale = () => {
      if (!wrapperRef.current) return

      const wrapperRect = wrapperRef.current.getBoundingClientRect()

      const availableSize = Math.min(wrapperRect.width, wrapperRect.height)

      // Calculate maxScale to fit within availableSize, with a bit of padding (e.g., 10%)
      const calculatedMaxScale = (availableSize / ANIMATION_SIZE) * 0.5
      setMaxScale(Math.max(1, calculatedMaxScale))
    }

    calculateMaxScale()

    window.addEventListener('resize', calculateMaxScale)
    return () => {
      window.removeEventListener('resize', calculateMaxScale)
    }
  }, [])

  useEffect(() => {
    let updateAnimationTimeId: NodeJS.Timeout

    if (animationDivRef.current) {
      animationDivRef.current.style.width = `0px`
      animationDivRef.current.style.height = `0px`
      animationDivRef.current.style.filter = `blur(40px)`
    }

    const listener = (event: CustomEvent<VoiceAnimationEvent>) => {
      if (!animationDivRef.current) {
        return
      }

      if (event.detail.type === VoiceAnimationEnum.playing) {
        const waveform = event.detail.waveform
        const analyser = event.detail.analyser

        const MIN_AMPLITUDE = 0.1
        const MAX_AMPLITUDE = 0.7

        const updateAnimation = () => {
          if (!animationDivRef.current) {
            return
          }

          analyser.getByteTimeDomainData(waveform)

          const avgAmplitude =
            waveform.reduce((sum, value) => sum + (value - 128), 0) / waveform.length

          let normalized = Math.abs(avgAmplitude)

          if (normalized < MIN_AMPLITUDE) normalized = MIN_AMPLITUDE
          if (normalized > MAX_AMPLITUDE) normalized = MAX_AMPLITUDE
          const calculatedScale =
            1 + ((normalized - MIN_AMPLITUDE) / (MAX_AMPLITUDE - MIN_AMPLITUDE)) * (maxScale - 1) // Scale from 1 to dynamic maxScale

          const calculatedSize = ANIMATION_SIZE * calculatedScale

          animationDivRef.current.style.width = `${Math.round(calculatedSize * 100) / 100}px`
          animationDivRef.current.style.height = `${Math.round(calculatedSize * 100) / 100}px`
        }

        updateAnimation()

        updateAnimationTimeId = setInterval(updateAnimation, 300)
      }

      if (event.detail.type === VoiceAnimationEnum.reset) {
        clearInterval(updateAnimationTimeId)

        animationDivRef.current.style.width = `0px`
        animationDivRef.current.style.height = `0px`
        animationDivRef.current.style.filter = `blur(40px)`
      }
    }

    window.addEventListener(EVENT_VOICE_ANIMATION_FOR_INTERVIEW_PREP, listener as EventListener)

    return () => {
      clearInterval(updateAnimationTimeId)
      window.removeEventListener(
        EVENT_VOICE_ANIMATION_FOR_INTERVIEW_PREP,
        listener as EventListener,
      )
    }
  }, [maxScale])

  return (
    <Wrapper ref={wrapperRef}>
      <Animation ref={animationDivRef} />
      <Children>{children}</Children>
    </Wrapper>
  )
}
